<template>
  <section class="kds-section-block" :class="`kds-section-block--${headingLevel}`">
    <div class="kds-section-block__header">
      <div class="kds-section-block__heading-group">
        <component
          :is="headingLevel"
          class="kds-section-block__title"
        >
          {{ title }}
        </component>
        <slot name="subtitle">
          <span
            v-if="subtitle"
            class="kds-section-block__subtitle"
          >{{ subtitle }}</span>
        </slot>
        <span
          v-if="$slots['title-suffix']"
          class="kds-section-block__title-suffix"
        >
          <slot name="title-suffix" />
        </span>
      </div>

      <template v-if="ctaLabel">
        <a
          v-if="ctaHref"
          class="kds-section-block__cta"
          :href="ctaDisabled ? undefined : ctaHref"
          :aria-disabled="ctaDisabled ? ariaDisabledTrue : undefined"
          :tabindex="ctaDisabled ? -1 : undefined"
          @click="onCtaLinkClick"
        >
          <span class="kds-section-block__cta-label">{{ ctaLabel }}</span>
          <KdsIcon
            name="arrow-right"
            :size="16"
            class="kds-section-block__cta-icon"
          />
        </a>
        <button
          v-else
          type="button"
          class="kds-section-block__cta"
          :disabled="ctaDisabled"
          :aria-disabled="ctaDisabled ? ariaDisabledTrue : undefined"
          @click="onCtaClick"
        >
          <span class="kds-section-block__cta-label">{{ ctaLabel }}</span>
          <KdsIcon
            name="arrow-right"
            :size="16"
            class="kds-section-block__cta-icon"
          />
        </button>
      </template>
    </div>

    <div class="kds-section-block__body">
      <template v-if="empty">
        <slot name="empty">
          <div
            class="kds-section-block__empty"
            :role="liveRole"
          >
            <p class="kds-section-block__empty-title">{{ resolvedEmptyTitle }}</p>
            <p
              v-if="emptyDescription"
              class="kds-section-block__empty-description"
            >{{ emptyDescription }}</p>
          </div>
        </slot>
      </template>
      <template v-else>
        <slot />
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, onMounted, ref, useSlots } from 'vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';

/**
 * KdsSectionBlock — section header (title + mono subtitle + CTA) + content slot
 * + empty state. Pure layout primitive: no card chrome, no i18n inside.
 * See docs/features/section-block/spec.md.
 */

export interface Props {
  /** Required section title, sentence case expected, already localised. */
  title: string;
  /** Mono ALL CAPS subtitle, already localised. Middle dot `·` is the only allowed separator. */
  subtitle?: string;
  /** ALL CAPS mono CTA label, already localised. */
  ctaLabel?: string;
  /** When provided, CTA renders as an anchor tag. Mutually exclusive with @cta-click. */
  ctaHref?: string;
  /** Semantic heading level for the title. */
  headingLevel?: 'h2' | 'h3' | 'h4';
  /** Force the empty state in place of the default slot content. */
  empty?: boolean;
  /** Empty state title; falls back to a generic label when omitted. */
  emptyTitle?: string;
  /** Empty state description (rendered under the empty title). */
  emptyDescription?: string;
  /**
   * Disable the CTA (D1, CA-12). When `true`:
   * - button mode: the native `disabled` attribute is set (plus `aria-disabled`).
   * - anchor mode: `aria-disabled="true"` + `tabindex="-1"` + `href` removed, clicks prevented.
   * The existing `[aria-disabled="true"]` style is applied in both cases.
   */
  ctaDisabled?: boolean;
}

// Workaround: guillemets simples dans les expressions de binding Vue causent une erreur
// de parsing avec le compilateur Vue prod (@vue/compiler-core cjs.prod) sur Windows.
// On extrait la valeur comme constante pour eviter les guillemets dans le template.
const ariaDisabledTrue = 'true';

const props = withDefaults(defineProps<Props>(), {
  subtitle: undefined,
  ctaLabel: undefined,
  ctaHref: undefined,
  headingLevel: 'h2',
  empty: false,
  emptyTitle: undefined,
  emptyDescription: undefined,
  ctaDisabled: false
});

const emit = defineEmits<{
  'cta-click': [event: MouseEvent];
}>();

const slots = useSlots();

// Initial mount flag — empty state gets role="status" only after first render
// to avoid spamming screen readers when the component lands in empty state at mount.
const isInitialMount = ref(true);
onMounted(() => {
  void nextTick(() => {
    isInitialMount.value = false;
  });
});

const liveRole = computed<'status' | undefined>(() => (isInitialMount.value ? undefined : 'status'));

const resolvedEmptyTitle = computed(() => props.emptyTitle ?? 'Nothing to show yet.');

// Dev-only warnings: ctaHref + @cta-click conflict, and empty=true with no title.
if (import.meta.env.DEV) {
  const instance = getCurrentInstance();
  const hasCtaClickListener = Boolean(instance?.vnode.props?.onCtaClick);
  if (props.ctaHref && hasCtaClickListener) {
    // eslint-disable-next-line no-console
    console.warn(
      '[KdsSectionBlock] `ctaHref` and `@cta-click` are mutually exclusive. ' +
        '`ctaHref` wins and the component renders as an anchor. Remove one of them.'
    );
  }
  if (props.empty && !props.emptyTitle && !slots.empty) {
    // eslint-disable-next-line no-console
    console.warn(
      '[KdsSectionBlock] `empty` is true but `emptyTitle` is missing. ' +
        'Falling back to the generic label. Provide `emptyTitle` or the `empty` slot.'
    );
  }
}

const onCtaClick = (event: MouseEvent): void => {
  if (props.ctaDisabled) {
    event.preventDefault();
    return;
  }
  emit('cta-click', event);
};

// Link-mode handler: when disabled, prevent navigation. We do not emit `cta-click`
// in link mode either way (the consumer relies on the browser following ctaHref).
const onCtaLinkClick = (event: MouseEvent): void => {
  if (props.ctaDisabled) {
    event.preventDefault();
  }
};
</script>

<style scoped lang="scss">
.kds-section-block {
  width: 100%;

  &__header {
    display: flex;
    align-items: baseline;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  &__heading-group {
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
    min-width: 0;
  }

  &__title-suffix {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    line-height: 1;
  }

  &__title {
    margin: 0;
    font-family: var(--font-ui);
    font-weight: var(--fw-semibold);
    color: var(--ink);
    font-size: var(--fs-h2);
    line-height: var(--lh-h2);
    letter-spacing: var(--ls-h2);
  }

  &--h3 &__title {
    font-size: var(--fs-h3);
    line-height: var(--lh-h3);
    letter-spacing: var(--ls-h3);
  }

  // No --fs-h4 / --lh-h4 / --ls-h4 token in kds-tokens.scss — fallback to h3 sizing.
  &--h4 &__title {
    font-size: var(--fs-h3);
    line-height: var(--lh-h3);
    letter-spacing: var(--ls-h3);
  }

  &__subtitle {
    font-family: var(--font-mono);
    font-size: var(--fs-meta);
    line-height: var(--lh-meta);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--stone);
  }

  &__cta {
    margin-left: auto;
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-2);
    max-width: 100%;
    font-family: var(--font-mono);
    font-size: var(--fs-meta);
    line-height: var(--lh-meta);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink);
    text-decoration: none;
    background: transparent;
    border: none;
    border-radius: var(--r-2);
    cursor: pointer;
    transition: color var(--d-fast) var(--ease-out);

    &:hover {
      color: var(--ink-3);
    }

    &:hover .kds-section-block__cta-icon {
      transform: translateX(2px);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }

    &:active {
      color: var(--ink-2);
    }

    &[aria-disabled="true"] {
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    }
  }

  &__cta-label {
    max-width: 18ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__cta-icon {
    transition: transform var(--d-fast) var(--ease-out);
  }

  &__body {
    padding-top: var(--space-4);
  }

  &__empty {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-6) 0;
  }

  &__empty-title {
    margin: 0;
    font-family: var(--font-ui);
    font-weight: var(--fw-medium);
    color: var(--ink-2);
  }

  &__empty-description {
    margin: 0;
    font-family: var(--font-mono);
    font-size: var(--fs-meta);
    line-height: var(--lh-meta);
    color: var(--stone);
  }

  @media (max-width: 639px) {
    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-2);
    }

    &__cta {
      margin-left: 0;
      margin-top: var(--space-1);
    }

    &__subtitle {
      max-width: 100%;
      white-space: normal;
    }
  }
}
</style>
